import { useEffect } from 'react';
import Navbar from './components/navbar.mjs';
import ProfileRequired from './components/ProfileRequired.mjs';
import ClaimsOpenRequired from './components/ClaimsOpenRequired.mjs';
import SuspendedBanner from './components/SuspendedBanner.mjs';
import AuthProvider from './authProvider.mjs';
import DialogProvider from './components/dialog/DialogProvider.mjs';
import ErrorBoundary from './components/ErrorBoundary.mjs';
import SessionTimeoutWarning from './components/SessionTimeoutWarning.mjs';
import TestEnvironmentBanner from './components/TestEnvironmentBanner.mjs';
import TestRoleSwitcher from './components/TestRoleSwitcher.mjs';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { LoginPage } from './home/login/page/login.mjs';
import { SignupPage } from './home/signup/page/signup.mjs';
import PerDiemForm from './perdiemLodging/page/perdiemLodging.mjs';
import ExpenseFormPage from './expenseReimbursement/page/expenseReimbursementFormPage.mjs';
import HomePage from './home/homepage/home.mjs';
import UploadPage from './dataUpload/page/dataUploadPage.mjs';
import AuthRequired from './authRequired.mjs';
import ClaimsDashboard from './dashboard/page/dashboardPage.mjs';
import ProfilePage from './profile/page/profilePage.mjs';
import AdminClaimDetail from './adminDashboard/page/adminClaimDetail.mjs';
import AdminDashboard from './adminDashboard/page/adminDashboard.mjs';
import AdminExpenseEdit from './adminDashboard/page/AdminExpenseEdit.mjs';
import AdminPerDiemEdit from './adminDashboard/page/AdminPerDiemEdit.mjs';
// import { EmailVerificationPage } from './home/signup/page/signupVerificationPage.mjs'; // DISABLED: email verification removed
import AdminSelectionDashboard from './adminSelection/page/adminSelectionPage.mjs';
import MonthlyReportGenerator from './auditor/page/MonthlyReportGenerator.mjs';
import MonthlyReportPage from './auditor/page/MonthlyReportPage.mjs';
import AuditorDashboard from './auditor/page/AuditorDashboard.mjs';
import PTAEManagement from './auditor/page/PTAEManagement.mjs';
import PTAERegions from './auditor/page/PTAERegions.mjs';
import PTAEApprovals from './auditor/page/PTAEApprovals.mjs';
import AdminMonthlyReportDashboard from './adminDashboard/page/adminMonthlyReports.mjs';
import AuthCallback from './auth/page/AuthCallback.mjs';
import CompleteProfile from './auth/page/CompleteProfile.mjs';

import RoleDashboardPage from './home/roleDashboard/roleDashboard.mjs';
// import PasswordReset from './home/passwordReset/page/passwordResetPage.mjs'; // DISABLED: password reset removed - auth via Google OAuth + Stanford SAML (no local passwords)
import RegionalDirectorDashboard from './director/page/RegionalDirectorDashboard.mjs';
import FileClaimForUser from './adminDashboard/page/FileClaimForUser.mjs';
import PerDiemOnBehalf from './adminDashboard/page/PerDiemOnBehalf.mjs';
import GlobalDirectorDashboard from './director/page/GlobalDirectorDashboard.mjs';
import ReportsPage from './reports/page/ReportsPage.mjs';
import { RegionalDirectorRequired, GlobalDirectorRequired, FinanceDirectorRequired, RoleRequired, ReportReviewStaffRequired, AdminRequired } from './components/RoleRequired.mjs';
import UserManagementPage from './userManagement/page/UserManagementPage.mjs';
import ClarificationsInbox from './clarifications/page/ClarificationsInbox.mjs';
import ClarificationThreadPage from './clarifications/page/ClarificationThreadPage.mjs';
import SignatureProvider from './signatures/SignatureProvider.mjs';
import PhoneSignPage from './signatures/page/PhoneSignPage.mjs';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;


function App() {
  // Initialize user preferences from localStorage on app start
  useEffect(() => {
    // Dark mode
    const darkMode = localStorage.getItem('darkMode') === 'true';
    if (darkMode) {
      document.documentElement.classList.add('dark');
    }

    // Compact view
    const compactView = localStorage.getItem('compactView') === 'true';
    if (compactView) {
      document.documentElement.classList.add('compact-view');
    }
  }, []);

  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || ''}>
        <Router>
          <AuthProvider>
          <DialogProvider>
          <SignatureProvider>
          <TestEnvironmentBanner />
          <TestRoleSwitcher />
          <SessionTimeoutWarning />
          <Navbar menuExtra={<TestRoleSwitcher variant="menu" />} />
          <SuspendedBanner />
          <Routes>
            {/* Public routes */}
            <Route path='/login' element={<LoginPage />} />
            <Route path='/signup' element={<SignupPage />} />
            <Route path='/' element={<HomePage />} />
            {/* <Route path="/verify-email" element={<EmailVerificationPage />} /> */}{/* DISABLED: email verification removed */}
            {/* <Route path='/forgot-password' element={<PasswordReset />} /> */}{/* DISABLED: password reset removed - auth via Google OAuth + Stanford SAML */}

            {/* Signing a report on a phone - not signed in; the link is the
                only authority, and it only hands a drawing back to the
                computer that showed it. */}
            <Route path='/sign' element={<PhoneSignPage />} />
            <Route path='/sign/:token' element={<PhoneSignPage />} />

            {/* OAuth routes */}
            <Route path='/auth/callback' element={<AuthCallback />} />
            <Route path='/complete-profile' element={
              <AuthRequired>
                <CompleteProfile />
              </AuthRequired>
            } />
    
          {/* Auditor routes - Fixed to use element prop */}
          <Route path="/generate-monthly-report" element={
            <AuthRequired>
              <MonthlyReportGenerator />
            </AuthRequired>
          } />

          <Route path="/dashboard/monthly-reports" element={
            <AuthRequired>
              <AdminMonthlyReportDashboard />
            </AuthRequired>
          } />
          <Route path="/monthly-report/:reportId" element={
            <AuthRequired>
              <MonthlyReportPage />
            </AuthRequired>
          } />
          {/* Staff only. Was AuthRequired alone, so a claimant could open the
              whole auditor dashboard by typing the address. Not AuditorRequired
              either: an auditor's stand-in holds a different staff role, and
              the server already serves them this data for the regions they
              cover. */}
          <Route path="/auditor-dashboard" element={
            <ReportReviewStaffRequired>
              <AuditorDashboard />
            </ReportReviewStaffRequired>
          } />
          {/* Landing: one card per region. The per-region page below does the
              actual management, so a region is always explicit in the URL.
              The auditor asks for changes; the finance director views every
              region and approves (admins no longer manage PTAEs). */}
          <Route path="/ptae-management" element={
            <AuthRequired>
              <RoleRequired roles={['auditor', 'finance_director']}>
                <PTAERegions />
              </RoleRequired>
            </AuthRequired>
          } />
          <Route path="/ptae-management/:region" element={
            <AuthRequired>
              <RoleRequired roles={['auditor', 'finance_director']}>
                <PTAEManagement />
              </RoleRequired>
            </AuthRequired>
          } />
          {/* Only the finance director decides chart-of-accounts changes. */}
          <Route path="/ptae-approvals" element={
            <AuthRequired>
              <RoleRequired roles={['finance_director']}>
                <PTAEApprovals />
              </RoleRequired>
            </AuthRequired>
          } />

          {/* Director routes */}
          <Route path="/regional-director-dashboard" element={
            <AuthRequired>
              <RegionalDirectorRequired>
                <RegionalDirectorDashboard />
              </RegionalDirectorRequired>
            </AuthRequired>
          } />
          <Route path="/global-director-dashboard" element={
            <AuthRequired>
              <GlobalDirectorRequired>
                <GlobalDirectorDashboard />
              </GlobalDirectorRequired>
            </AuthRequired>
          } />
          <Route path="/finance-director-dashboard" element={
            <AuthRequired>
              <FinanceDirectorRequired>
                <GlobalDirectorDashboard />
              </FinanceDirectorRequired>
            </AuthRequired>
          } />

          {/* Reports - read-only records and reporting; what each role sees is
              scoped by the server */}
          <Route path="/reports" element={
            <AuthRequired>
              <ReportReviewStaffRequired>
                <ReportsPage />
              </ReportReviewStaffRequired>
            </AuthRequired>
          } />

          {/* Clarification questions - every signed-in user, since claimants
              answer the questions admins put to them. Which threads each
              person sees, and what they may do in them, is decided by the
              server: a claimant gets only the ones put to them. The thread
              route is where every question email links. */}
          <Route path="/clarifications" element={
            <AuthRequired>
              <ClarificationsInbox />
            </AuthRequired>
          } />
          <Route path="/clarifications/:threadId" element={
            <AuthRequired>
              <ClarificationThreadPage />
            </AuthRequired>
          } />

          {/* User Management - accessible by admin, regional_director, global_director, finance_director */}
          <Route path="/user-management" element={
            <AuthRequired>
              <RoleRequired roles={['admin', 'regional_director', 'global_director', 'finance_director']}>
                <UserManagementPage />
              </RoleRequired>
            </AuthRequired>
          } />

          {/* Role dashboards - the home page's role card opens one of these:
              cards for the role's approval work, anything delegated to the
              person, and the role's other tools (RoleToolCards). The approval
              pages above keep their own addresses, which emails and the
              monthly report's Back buttons use. */}
          <Route path="/admin/links" element={
            <AuthRequired>
              <AdminRequired>
                <RoleDashboardPage />
              </AdminRequired>
            </AuthRequired>
          } />
          <Route path="/auditor" element={
            <AuthRequired>
              <RoleRequired roles={['auditor']}>
                <RoleDashboardPage />
              </RoleRequired>
            </AuthRequired>
          } />
          <Route path="/regional-director" element={
            <AuthRequired>
              <RegionalDirectorRequired>
                <RoleDashboardPage />
              </RegionalDirectorRequired>
            </AuthRequired>
          } />
          <Route path="/global-director" element={
            <AuthRequired>
              <RoleRequired roles={['global_director']}>
                <RoleDashboardPage />
              </RoleRequired>
            </AuthRequired>
          } />
          <Route path="/finance-director" element={
            <AuthRequired>
              <FinanceDirectorRequired>
                <RoleDashboardPage />
              </FinanceDirectorRequired>
            </AuthRequired>
          } />
          
          {/* Protected routes - only accessible when logged in */}
          <Route path='/profile' element={
            <AuthRequired>
              <ProfilePage />
            </AuthRequired>
          } />
          
          {/* ClaimsOpenRequired: production takes claims from East Africa
              only for now; other regions are told it is coming soon. */}
          <Route path='/perdiem/create' element={
            <AuthRequired>
              <ProfileRequired>
                <ClaimsOpenRequired>
                  <PerDiemForm />
                </ClaimsOpenRequired>
              </ProfileRequired>
            </AuthRequired>
          } />

          <Route path="/admin" element={
            <AuthRequired>
               <AdminSelectionDashboard />
            </AuthRequired>
          } />
          
          {/* Invoice upload moved into the expense form - redirect the old page. */}
          <Route path='/claim/invoice-upload' element={<Navigate to="/claim/expense" replace />} />
          
          <Route path="/claim/expense/:claimId?" element={
            <AuthRequired>
              <ProfileRequired>
                <ClaimsOpenRequired allowExisting>
                  <ExpenseFormPage />
                </ClaimsOpenRequired>
              </ProfileRequired>
            </AuthRequired>
          } />
          <Route path="/expense/claims/:claimId?" element={
            <AuthRequired>
              <ExpenseFormPage />
            </AuthRequired>
          } />
          <Route path='/admin/expense/claims/:claimId' element={
            <AuthRequired>
              <AdminExpenseEdit />
            </AuthRequired>
          } />
          
          <Route path='/data-upload' element={
            <AuthRequired>
              <UploadPage />
            </AuthRequired>
          } />

          <Route path='/dashboard' element={
            <AuthRequired>
              <ClaimsDashboard />
            </AuthRequired>
          } />
          
          <Route path='/claim/perdiem-lodging/:claimId' element={
            <AuthRequired>
              <PerDiemForm/>
            </AuthRequired>
          } />
          
          <Route path='/admin/perdiem/claims/:claimId' element={
            <AuthRequired>
              <AdminPerDiemEdit/>
            </AuthRequired>
          } />

          <Route path='/admin/detail' element={
            <AuthRequired>
              <AdminClaimDetail />
            </AuthRequired>
          } />
          
          <Route path='/admin/dashboard' element={
            <AuthRequired>
              <AdminDashboard />
            </AuthRequired>
          } />

          {/* Filing a claim for someone in your region. AdminRequired rather
              than the bare AuthRequired the older admin routes use: this one
              writes a claim in another person's name, so the page should not
              render for someone who could never be allowed to submit it. The
              server is still the authority - it checks the role AND the
              region on every request. */}
          <Route path='/admin/file-claim' element={
            <AdminRequired>
              <ClaimsOpenRequired>
                <FileClaimForUser />
              </ClaimsOpenRequired>
            </AdminRequired>
          } />

          {/* The ordinary per diem form, filing for someone. Under /perdiem/create
              rather than /admin/ on purpose: the form switches into ADMIN mode
              for any path containing /admin/ - the edit-an-existing-claim mode,
              with no Submit - and this has to behave as the claimant's form. */}
          <Route path='/perdiem/create/on-behalf' element={
            <AdminRequired>
              <ClaimsOpenRequired>
                <PerDiemOnBehalf />
              </ClaimsOpenRequired>
            </AdminRequired>
          } />

          <Route path='/admin/expense/claims/:claimId' element={
            <AuthRequired>
              <ExpenseFormPage />
            </AuthRequired>
          } />

          <Route path='/admin/claim/perdiem-lodging/:claimId' element={
            <AuthRequired>
              <PerDiemForm />
            </AuthRequired>
          } />
            </Routes>
          </SignatureProvider>
          </DialogProvider>
          </AuthProvider>
        </Router>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}

export default App;

